/* ERAK Estimator — client-side mirror of backend engine.py.
   Money is integer FILS (1 AED = 100 fils). Mirrors ROUND_HALF_UP + largest-remainder. */

export const CATEGORIES = [
  'MATERIAL', 'MANPOWER', 'SUPERVISORS', 'VEHICLE',
  'TOOLS & EQUIPMENT', 'SUBCONTRACTOR', 'SAFETY', 'MISC',
];
// P3 — single canonical category order used everywhere. Lookup index for sorting.
export const CATEGORY_ORDER = Object.fromEntries(CATEGORIES.map((c, i) => [c, i]));

export function orderCategories(categories) {
  // Sort cost-BOQ categories into the canonical order; unknown ones go last.
  return [...(categories || [])].sort(
    (a, b) => (CATEGORY_ORDER[a.name] ?? CATEGORIES.length) - (CATEGORY_ORDER[b.name] ?? CATEGORIES.length),
  );
}

export function roundHalfUp(x) {
  // round half away from zero (matches Decimal ROUND_HALF_UP for positives)
  return Math.sign(x) * Math.round(Math.abs(x) + 1e-9);
}

export function aedToFils(aed) {
  const n = Number(aed) || 0;
  return roundHalfUp(n * 100);
}

export function filsToAed(fils) {
  return (Number(fils) || 0) / 100;
}

function pct(amountFils, p) {
  return roundHalfUp((Number(amountFils) || 0) * (Number(p) || 0));
}

export function resolveRate(code, masters) {
  if (!code) return { rate: null, meta: null, resolved: false };
  const cu = String(code).toUpperCase();
  // Codes are unique across master types — flat lookup, decoupled from prefix.
  for (const t of Object.keys(masters || {})) {
    const item = masters[t] && masters[t][cu];
    if (item) {
      if (item.active === false) return { rate: null, meta: null, resolved: false };
      return { rate: aedToFils(item.rate), meta: item, resolved: true };
    }
  }
  return { rate: null, meta: null, resolved: false };
}

function resolveBrand(meta, brandId) {
  if (!brandId || !meta) return { rate: null, name: null };
  const b = (meta.brands || []).find((x) => x.id === brandId && x.active !== false);
  if (b && b.rate !== null && b.rate !== undefined) return { rate: b.rate, name: b.name };
  return { rate: null, name: null };
}

export function computeLine(line, saleQty, masters, catName = null, applyK = false) {
  const code = (line.code || '').trim();
  const brandId = line.brandId || null;
  const qpu = Number(line.qtyPerUnit) || 0;
  const { rate, meta, resolved } = code ? resolveRate(code, masters) : { rate: null, meta: null, resolved: false };

  let r, description, unit, linked, unresolved, brandName = null;
  if (resolved) {
    r = rate;
    description = line.description || meta.description || meta.trade;
    unit = line.unit || meta.unit;
    // Brand override (materials only) — brand rate replaces base when selected.
    const br = resolveBrand(meta, brandId);
    if (br.rate !== null && br.rate !== undefined) { r = aedToFils(br.rate); brandName = br.name; }
    linked = true;
    unresolved = false;
  } else {
    r = aedToFils(line.rate || 0);
    description = line.description || '';
    unit = line.unit || '';
    linked = false;
    unresolved = !!code;
  }
  // --- K factor (MATERIAL lines only) — market-price adjustment ---
  const isMaterial = catName === 'MATERIAL';
  const masterRate = r; // reference rate in fils
  const hasK = line.kValue !== null && line.kValue !== undefined && line.kValue !== '';
  const kValue = (isMaterial && hasK) ? aedToFils(line.kValue) : masterRate;
  const kPct = masterRate ? Math.round(((kValue - masterRate) / masterRate) * 100 * 1e4) / 1e4 : 0;
  const appliedRate = (isMaterial && applyK) ? kValue : masterRate;

  // Every cost line scales with the client quantity (standard variable BOQ).
  const sq = saleQty ? Number(saleQty) : 1;
  const amount = roundHalfUp(appliedRate * qpu * sq);
  return {
    lineId: line.lineId, code, brandId, brandName, description, unit, qtyPerUnit: qpu,
    rate: appliedRate, masterRate, kValue, kPct, isMaterial, amount, linked, unresolved,
    activity: line.activity,
  };
}

// Returns a list of human-readable validation issues (negative qty / rate).
export function findValidationIssues(draft) {
  const issues = [];
  for (const si of draft.saleItems || []) {
    const itemNo = si.itemNo || '?';
    if ((Number(si.qty) || 0) <= 0) issues.push(`${itemNo}: quantity must be greater than zero`);
    for (const cat of si.costBoq?.categories || []) {
      for (const ln of cat.lines || []) {
        const label = ln.code || ln.description || 'line';
        if ((Number(ln.qtyPerUnit) || 0) < 0) issues.push(`${itemNo} · ${cat.name} · ${label}: negative quantity`);
        if ((Number(ln.rate) || 0) < 0) issues.push(`${itemNo} · ${cat.name} · ${label}: negative rate`);
      }
    }
  }
  return issues;
}

export function computeSaleItem(saleItem, masters, applyK = false) {
  const qty = Number(saleItem.qty) || 1;
  const outCats = [];
  let totalCost = 0;
  for (const cat of orderCategories(saleItem.costBoq?.categories || [])) {
    const lines = [];
    let subtotal = 0;
    for (const ln of cat.lines || []) {
      const cl = computeLine(ln, qty, masters, cat.name, applyK);
      lines.push(cl);
      subtotal += cl.amount;
    }
    outCats.push({ name: cat.name, lines, subtotal });
    totalCost += subtotal;
  }
  const effUnit = qty ? roundHalfUp(totalCost / qty) : 0;
  return {
    itemId: saleItem.itemId, itemNo: saleItem.itemNo, description: saleItem.description,
    unit: saleItem.unit, qty, costBoq: { categories: outCats },
    totalCost, effectiveUnitCost: effUnit,
  };
}

export function computeMarkupLadder(directCost, markup) {
  const A = Math.round(directCost);
  const B = pct(A, markup.escalationPct);
  const C = pct(A, markup.contingencyPct);
  const D = pct(A, markup.adminOhPct);
  const finalCost = A + B + C + D;
  const E = pct(finalCost, markup.profitPct);
  const F = pct(finalCost, markup.negotiationPct);
  const quotation = finalCost + E + F;
  return {
    directCost: A, escalation: B, contingency: C, adminOh: D,
    finalCostPrice: finalCost, profit: E, negotiation: F,
    quotationPrice: quotation, markupFactor: A ? Math.round((quotation / A) * 1e6) / 1e6 : 0,
  };
}


// P5 — pro-rata cost-weighted allocation; residue → largest-weight bucket.
// BigInt floor division keeps this fils-exact and identical to engine.py's Decimal.
function allocateLargest(total, weights) {
  const n = weights.length;
  if (n === 0) return [];
  const wsum = weights.reduce((a, b) => a + b, 0);
  if (wsum <= 0) {
    const base = Math.floor(total / n);
    const out = weights.map(() => base);
    out[0] += total - base * n;
    return out;
  }
  const T = BigInt(total); const W = BigInt(wsum);
  const shares = weights.map((w) => Number((T * BigInt(w)) / W));
  const residue = total - shares.reduce((a, b) => a + b, 0);
  let big = 0;
  for (let i = 1; i < n; i++) if (weights[i] > weights[big]) big = i;
  shares[big] += residue;
  return shares;
}

export function computeProject(draft, masters, vatPct = 0, marginBasis = 'profit', applyK = null) {
  const ak = applyK === null || applyK === undefined ? !!draft.applyK : !!applyK;
  const markup = draft.markup || {};
  const rawItems = draft.saleItems || [];
  const items = rawItems.map((si) => computeSaleItem(si, masters, ak));
  const directCost = items.reduce((a, ci) => a + ci.totalCost, 0);

  // --- P5: overhead build-up + per-line profit ---
  // Escalation / Contingency / Admin OH = global % of direct cost, allocated
  // pro-rata (cost-weighted) onto each line; residue → largest-cost line.
  // Profit is a PER-LINE % (back-filled from legacy markup.profitPct). Negotiation
  // stays a global % applied per line on its post-overhead final cost.
  const escTotal = pct(directCost, markup.escalationPct);
  const contTotal = pct(directCost, markup.contingencyPct);
  const adminTotal = pct(directCost, markup.adminOhPct);
  const weights = items.map((ci) => ci.totalCost);
  const escAlloc = allocateLargest(escTotal, weights);
  const contAlloc = allocateLargest(contTotal, weights);
  const adminAlloc = allocateLargest(adminTotal, weights);

  const defaultProfitPct = markup.profitPct || 0;
  const negPct = markup.negotiationPct || 0;

  let totalProfit = 0; let totalNegotiation = 0; let quotation = 0;
  items.forEach((ci, i) => {
    const finalCostLine = ci.totalCost + escAlloc[i] + contAlloc[i] + adminAlloc[i];
    const raw = rawItems[i] || {};
    const p = (raw.profitPct === undefined || raw.profitPct === null) ? defaultProfitPct : raw.profitPct;
    const profitLine = pct(finalCostLine, p);
    const negLine = pct(finalCostLine, negPct);
    const sellLine = finalCostLine + profitLine + negLine;
    ci.escalation = escAlloc[i];
    ci.contingency = contAlloc[i];
    ci.adminOh = adminAlloc[i];
    ci.finalCost = finalCostLine;
    ci.profitPct = p;
    ci.profit = profitLine;
    ci.negotiation = negLine;
    ci.sellPrice = sellLine;
    ci.sellUnitPrice = ci.qty ? roundHalfUp(sellLine / ci.qty) : 0;
    totalProfit += profitLine;
    totalNegotiation += negLine;
    quotation += sellLine;
  });

  const finalCostPrice = directCost + escTotal + contTotal + adminTotal;
  const ladder = {
    directCost, escalation: escTotal, contingency: contTotal, adminOh: adminTotal,
    finalCostPrice, profit: totalProfit, negotiation: totalNegotiation,
    quotationPrice: quotation,
    markupFactor: directCost ? Math.round((quotation / directCost) * 1e6) / 1e6 : 0,
    blendedProfitPct: finalCostPrice ? Math.round((totalProfit / finalCostPrice) * 1e6) / 1e6 : 0,
  };

  const costMix = {};
  for (const c of CATEGORIES) costMix[c] = 0;
  for (const ci of items) for (const cat of ci.costBoq.categories) costMix[cat.name] = (costMix[cat.name] || 0) + cat.subtotal;

  // Margin (configurable basis)
  let marginAmount;
  let basis = marginBasis;
  if (marginBasis === 'profit_negotiation') marginAmount = ladder.profit + ladder.negotiation;
  else if (marginBasis === 'gross') marginAmount = quotation - directCost;
  else { basis = 'profit'; marginAmount = ladder.profit; }
  const marginPct = quotation ? Math.round((marginAmount / quotation) * 10000) / 100 : 0;

  // Net Profit (fixed definition, independent of margin basis) — equals the blended Profit line.
  const netProfit = quotation - directCost - ladder.escalation - ladder.contingency - ladder.adminOh - ladder.negotiation;
  const netProfitPct = quotation ? Math.round((netProfit / quotation) * 10000) / 100 : 0;

  const vatAmount = vatPct ? pct(quotation, vatPct) : 0;
  const grandTotal = quotation + vatAmount;
  const unresolvedCount = items.reduce((a, ci) => a + ci.costBoq.categories.reduce(
    (b, cat) => b + cat.lines.filter((l) => l.unresolved).length, 0), 0);
  return {
    saleItems: items, markup, ladder, directCost, quotationPrice: quotation,
    markupFactor: ladder.markupFactor, blendedProfitPct: ladder.blendedProfitPct,
    costMix, marginPct, marginBasis: basis,
    netProfit, netProfitPct, vatPct, vatAmount,
    grandTotal, unresolvedCount, applyK: ak,
  };
}

// Soft pre-finalise warnings (B4) — advisory checklist before moving past Draft.
export function findPrefinaliseWarnings(computed) {
  const w = [];
  for (const ci of computed.saleItems || []) {
    const itemNo = ci.itemNo || '?';
    const hasActivity = (ci.costBoq?.categories || []).some((cat) => (cat.lines || []).some((ln) => ln.activity));
    if (!hasActivity) w.push(`${itemNo}: no activity applied — this line would ship at zero cost`);
    else if (!ci.totalCost) w.push(`${itemNo}: Cost BOQ total is zero / empty`);
    for (const cat of ci.costBoq?.categories || []) {
      for (const ln of cat.lines || []) {
        const rate = Number(ln.rate) || 0;
        const qpu = Number(ln.qtyPerUnit) || 0;
        const label = ln.code || ln.description || 'line';
        const where = `${itemNo} · ${cat.name} · ${label}`;
        if (rate > 0 && qpu === 0) w.push(`${where}: has a rate but zero quantity`);
        else if (qpu > 0 && rate === 0) w.push(`${where}: has a quantity but zero rate`);
        if ((rate > 0 || qpu > 0) && !(ln.description || '').trim()) w.push(`${where}: has values but no description`);
      }
    }
  }
  if ((computed.marginPct || 0) < 0) w.push('Overall tender margin is negative');
  return w;
}

export function buildMastersDict(byType) {
  // byType = { material: [...], vehicle: [...], ... } → { type: { CODE: doc } }
  const idx = (arr) => Object.fromEntries((arr || []).map((m) => [String(m.code).toUpperCase(), m]));
  const out = {};
  for (const t of Object.keys(byType || {})) out[t] = idx(byType[t]);
  return out;
}


/* --------------------------------------------------------------------------- *
 * P3 — Invoice Plan (Milestone / POC). All money in INTEGER FILS.
 * Source of truth per line = plannedPct. Everything else is DERIVED here.
 * Mirrors backend/engine.py compute_invoice_plan() EXACTLY.
 * --------------------------------------------------------------------------- */
export function computeInvoicePlan(plan, contractValue) {
  const cv = Math.trunc(Number(contractValue) || 0);
  const retentionDefault = Number(plan?.retentionPct) || 0; // decimal
  const advancePct = Number(plan?.advancePct) || 0;         // decimal
  const lines = [...(plan?.lines || [])].sort(
    (a, b) => ((a.seq ?? 0) - (b.seq ?? 0)),
  );

  const advanceTotal = pct(cv, advancePct); // fils

  const computed = lines.map((l) => {
    const plannedPct = Number(l.plannedPct) || 0; // percent number
    const gross = pct(cv, plannedPct / 100);
    const isAdv = !!l.isAdvance;
    let retPct;
    if (isAdv) retPct = 0;
    else if (l.retentionPct !== null && l.retentionPct !== undefined && l.retentionPct !== '') retPct = Number(l.retentionPct) / 100;
    else retPct = retentionDefault;
    const retention = isAdv ? 0 : pct(gross, retPct);
    return {
      seq: l.seq,
      description: l.description || '',
      trigger: l.trigger || '',
      plannedPct,
      plannedGross: gross,
      retentionPct: Math.round(retPct * 100 * 1e4) / 1e4,
      retentionAmount: retention,
      isAdvance: isAdv,
      advanceRecovery: 0,
      plannedNet: 0,
      plannedDate: l.plannedDate || '',
      status: l.status || 'Planned',
    };
  });

  const nonAdv = computed.map((c, i) => i).filter((i) => !computed[i].isAdvance);
  if (advanceTotal > 0 && nonAdv.length) {
    const base = nonAdv.reduce((a, i) => a + computed[i].plannedGross, 0);
    if (base > 0) {
      let allocated = 0;
      nonAdv.forEach((i, k) => {
        if (k < nonAdv.length - 1) {
          const share = Math.floor((advanceTotal * computed[i].plannedGross) / base);
          computed[i].advanceRecovery = share;
          allocated += share;
        } else {
          computed[i].advanceRecovery = advanceTotal - allocated; // last line reconciles
        }
      });
    }
  }

  computed.forEach((c) => {
    c.plannedNet = c.isAdvance ? c.plannedGross : c.plannedGross - c.retentionAmount - c.advanceRecovery;
  });

  const sumPct = Math.round(computed.reduce((a, c) => a + c.plannedPct, 0) * 1e6) / 1e6;
  const sumGross = computed.reduce((a, c) => a + c.plannedGross, 0);
  const totalRetention = computed.reduce((a, c) => a + c.retentionAmount, 0);
  const totalRecovered = computed.reduce((a, c) => a + c.advanceRecovery, 0);
  const totalNet = computed.reduce((a, c) => a + c.plannedNet, 0);

  return {
    planType: plan?.planType || 'MILESTONE',
    retentionPct: retentionDefault,
    advancePct,
    advanceRecovery: plan?.advanceRecovery || 'PRORATA',
    contractValue: cv,
    advanceTotal,
    lines: computed,
    totals: {
      plannedPct: sumPct,
      plannedGross: sumGross,
      retentionAmount: totalRetention,
      retentionPool: totalRetention,
      advanceRecovery: totalRecovered,
      plannedNet: totalNet,
      unallocatedPct: Math.round((100 - sumPct) * 1e6) / 1e6,
      unallocatedGross: cv - sumGross,
    },
    validation: {
      fullyAllocated: Math.abs(sumPct - 100) < 1e-6,
      advanceReconciled: totalRecovered === advanceTotal,
    },
  };
}

/* --------------------------------------------------------------------------- *
 * P4 — Cost-control dashboard. All money in INTEGER FILS; every figure derived.
 * Mirrors backend/engine.py compute_cost_control() EXACTLY.
 * --------------------------------------------------------------------------- */
function invoicedRollup(invoiceLines) {
  let raised = 0; let certified = 0; let paid = 0;
  for (const l of (invoiceLines || [])) {
    if (l.void) continue;
    const gross = Math.trunc(Number(l.plannedGross) || 0);
    const st = l.status || 'Planned';
    if (st === 'Raised') raised += gross;
    else if (st === 'Certified') certified += gross;
    else if (st === 'Paid') paid += gross;
  }
  return { raised, certified, paid, total: certified + paid };
}

export function computeCostControl(contractValue, categories, invoiceLines = null,
  earnedBottomUp = 0, hasCertifiedExecution = null) {
  const cv = Math.trunc(Number(contractValue) || 0);
  const cats = [];
  let totB = 0; let totC = 0; let totA = 0;
  for (const c of (categories || [])) {
    const b = Math.trunc(Number(c.budget) || 0);
    const cm = Math.trunc(Number(c.committed) || 0);
    const ac = Math.trunc(Number(c.actual) || 0);
    cats.push({
      category: c.category,
      budget: b, committed: cm, actual: ac,
      balanceToCommit: b - cm,
      balanceVsActual: b - ac,
      committedVsActual: cm - ac,
    });
    totB += b; totC += cm; totA += ac;
  }
  const budgetedMargin = cv - totB;
  const projectedMargin = cv - totA;
  const marginErosion = budgetedMargin - projectedMargin;
  const pctOf = (n) => (cv ? Math.round((n / cv) * 100 * 1e4) / 1e4 : 0);

  // --- WIP (Work-in-Progress) — Earned vs Cumulative Invoiced (all fils) ---
  const invoiced = invoicedRollup(invoiceLines);
  const cumulativeInvoiced = invoiced.total; // Certified + Paid
  const earnedBu = Math.trunc(Number(earnedBottomUp) || 0);
  const earnedPoc = totB ? roundHalfUp((totA / totB) * cv) : 0;
  const wipBottomUp = earnedBu - cumulativeInvoiced;
  const wipPoc = earnedPoc - cumulativeInvoiced;
  const hasExec = hasCertifiedExecution === null || hasCertifiedExecution === undefined
    ? earnedBu > 0 : !!hasCertifiedExecution;

  return {
    contractValue: cv,
    categories: cats,
    totals: {
      budget: totB, committed: totC, actual: totA,
      balanceToCommit: totB - totC,
      balanceVsActual: totB - totA,
      committedVsActual: totC - totA,
    },
    margins: {
      budgetedMargin, budgetedMarginPct: pctOf(budgetedMargin),
      projectedMargin, projectedMarginPct: pctOf(projectedMargin),
      marginErosion,
      erosionUnfavourable: marginErosion > 0,
    },
    cumulativeInvoiced: invoiced,
    wip: {
      earnedBottomUp: earnedBu,
      earnedPoc,
      cumulativeInvoiced,
      wipBottomUp,
      wipPoc,
      // Positive WIP = UNDER-billed (to invoice); negative = OVER-billed.
      underBilled: wipBottomUp > 0,
      overBilled: wipBottomUp < 0,
      hasCertifiedExecution: hasExec,
    },
  };
}

